import React, { useCallback, useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Shield,
  Calendar,
  DollarSign,
  CheckCircle,
  Loader2,
  AlertCircle,
  Clock,
  Send,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";

import { FlexRow } from "components/flex-row/flex-row";

import BaseButton from "components/base-button/base-button";
import BaseInput from "components/base-input/base-input";
import BaseSelect from "components/base-select/base-select";
import useModal from "hooks/use-modal";
import API from "../../utils/api";
import Select from "react-select";
import CurrencyInput from "../../components/currency-input/currency-input";
import classNames from "classnames";
import { calculatePaymentPlan } from "../../utils/paymentplan";
import { formatMoney } from "../../utils/formatMoney";
import { MainStore } from "../../stores/Main";
import { trackOfferSubmitted, trackPaymentSubmitted, trackSettlementResponse, calculateDaysUntil } from "../../utils/posthogTracking";

// Helpers
const formatMMDDYYYY = (date) => {
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const parseMMDDYYYY = (dateStr) => {
  if (!dateStr) return null;
  const [mm, dd, yyyy] = dateStr.split("/").map((v) => parseInt(v, 10));
  if (!mm || !dd || !yyyy) return null;
  return new Date(yyyy, mm - 1, dd);
};

const computeRecurringDate = (baseDate, recurringMethod, monthOffset) => {
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth() + monthOffset;
  let date;
  if (recurringMethod === "1st") {
    date = new Date(year, month, 1);
  } else if (recurringMethod === "15th") {
    date = new Date(year, month, 15);
  } else {
    // last day of month
    date = new Date(year, month + 1, 0);
  }
  return formatMMDDYYYY(date);
};

const getCreditorNameFromLabel = (label) => {
  if (!label) return "";
  const idx = label.indexOf(" (Balance");
  if (idx > -1) return label.substring(0, idx);
  return label;
};

const buildAchDisclosureParagraphs = (
  agencyName,
  methodDesc,
  creditorName,
  dateAuthorized,
  agencyWebsite,
  agencyAddressLine,
  agencyPhone
) => {
  // Matches Example-PaymentConfirmation.jsx.bak with agency substitution
  return [
    "Authorization to initiate ACH/electronic debit(s)",
    `On ${dateAuthorized}, I authorized ${agencyName} to initiate an ACH/electronic debit(s) from my bank account on record with the account, ${methodDesc}, for payment to my ${creditorName} account in accordance with the scheduled payment(s) listed below.`,
    `The above payments will be debited from my bank account on record, with the account, ${methodDesc}. I agree that ACH transactions I authorize comply with all applicable law.`,
    "I understand that this authorization will remain in full force and effect during the Payment Dates until I notify " +
      `${agencyName} that I wish to revoke this authorization by canceling this scheduled transaction either inside the website, ${agencyWebsite}, by writing to ` +
      `${agencyName} at ${agencyAddressLine}, or by calling ${agencyPhone}.`,
    "I understand that " +
      `${agencyName} requires at least three (3) day's notice prior to the next Payment Date in order to cancel this authorization. I understand I can also cancel my payment(s) by notifying my financial institution verbally or in writing at least 3 business days before the scheduled payment. If the above noted payment date falls on a weekend or holiday the payments may be executed on the next business day.`,
    `You may call us at ${agencyPhone} during normal business hours if you have any questions.`,
    "It is recommended that you print or take a screenshot of this authorization and maintain it for your records.",
  ];
};

const FormMakePayment = (props) => {
  let baseForm = {};
  let selectedAccount;
  if (props.form) {
    baseForm = props.form;
    selectedAccount = baseForm.account.value;
  } else {
    if (props.account?.id) {
      selectedAccount = props.account.id;
      baseForm.amount = props.amount;
    }
  }

  const mode = props.mode;
  const offerId = props.offerId;
  const selectedPlan = props.selectedPlan;
  const back = props.back;
  const backData = props.backData;

  const { openModal, closeModal, modalTypes } = useModal();
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(baseForm);
  const [methods, setMethods] = useState([]);
  const [error, setError] = useState();
  const [dateError, setDateError] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [feeLoading, setFeeLoading] = useState(false);
  const [receiptId, setReceiptId] = useState("");
  const [schedule, setSchedule] = useState(!!baseForm.date);
  const [step, setStep] = useState("payment");
  const [convFee, setConfFee] = useState(null);
  const [achVerifyError, setAchVerifyError] = useState(null);
  const [paymentResponse, setPaymentResponse] = useState(null);
  const [existingScheduledPayments, setExistingScheduledPayments] = useState([]);
  const mainStore = useContext(MainStore);

  const validDate = (idate) => {
    // Parse the input string to a Date object
    const inputDate = new Date(idate);

    // Get today's date with time set to 00:00:00 for accurate comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate the maximum allowed date (45 days from today)
    const maxDate = new Date();
    maxDate.setHours(0, 0, 0, 0);
    maxDate.setDate(maxDate.getDate() + 45);

    // Check if date is in the future but not more than 45 days ahead
    return inputDate > today && inputDate <= maxDate;
  };

  const setDate = (e) => {
    setForm({ ...form, date: e });
    if (e !== "" && !validDate(e)) {
      setDateError(true);
    } else {
      setDateError(false);
    }
  };

  const toggleSchedule = (e) => {
    console.log(e);
    setSchedule(e);
    if (!e && dateError) {
      setDateError(false);
    }
    if (!e) {
      setDate("");
    }
  };

  const fetchMethods = useCallback(async () => {
    try {
      const methodList = [];
      const response = await API.get("/payment-methods");
      if (response.data.payment_methods) {
        response.data.payment_methods.forEach((method) => {
          if (mainStore.agencyDetails.disable_check && method.type === "bank")
            return;
          let name = method.method_desc;
          methodList.push({
            value: method.id,
            label: name,
            def: method.default,
            new: false,
            // carry useful fields for downstream requests
            method_type: method.method_type,
            billing_zip: method.billing_zip,
            billing_state: method.billing_state,
          });
        });

        // Only set default method if no method is currently selected
        if (form.method === null || form.method === undefined) {
          const defaultMethod = methodList.find(({ def }) => def);
          if (defaultMethod) {
            setForm((prevState) => {
              return { ...prevState, method: defaultMethod };
            });
          }
        } else if (form.method && !form.method.new) {
          // If we have a selected method that's not "new", find and use the matching one from the list
          const matchingMethod = methodList.find(
            (m) => m.value === form.method.value
          );
          if (matchingMethod) {
            setForm((prevState) => {
              return { ...prevState, method: matchingMethod };
            });
          }
        }
      }
      methodList.push({
        value: "credit-card",
        label: "New Card",
        def: false,
        new: true,
      });
      if (!mainStore.agencyDetails.disable_check) {
        methodList.push({
          value: "bank-account",
          label: "New Bank",
          def: false,
          new: true,
        });
      }
      setMethods(methodList);
    } catch (e) {
      console.log(e);
    }
  }, [mainStore, form.method?.value]);

  const _closeModal = () => {
    closeModal();
    if (props.paymentComplete) {
      props.paymentComplete();
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await API.get("/accounts");
      const accountList = [];
      if (!response.data.result_code) {
        response.data.account.forEach((account) => {
          if (
            account.status === undefined ||
            account.status === 0 ||
            account.status === 1
          ) {
            const accountName =
              account.chargeoff_creditor?.name ||
              account.creditor?.name ||
              "Unknown Account";
            const refId =
              account.chargeoff_creditor?.ref_id ||
              account.creditor?.ref_id ||
              account.id;
            accountList.push({
              value: account.id,
              label:
                accountName +
                " #" +
                refId +
                " (Balance: $" +
                account.balance_current +
                ")",
              balance: account.balance_current,
              creditorName: accountName,
            });
          }
        });
        setForm((prevState) => {
          return {
            ...prevState,
            account: accountList.find(({ value }) => value === selectedAccount),
          };
        });
        setAccounts(accountList);
      }
    } catch (e) {}
  };

  const fetchExistingScheduledPayments = async (accountId) => {
    if (!accountId) return;
    try {
      const response = await API.get(`/accounts/${accountId}/scheduled-payments`);
      if (response.data.scheduled) {
        setExistingScheduledPayments(response.data.scheduled);
      } else {
        setExistingScheduledPayments([]);
      }
    } catch (e) {
      console.log(e);
      setExistingScheduledPayments([]);
    }
  };

  const nextStep = useCallback(async () => {
    if (schedule && (form.date === undefined || form.date === "")) return;
    if (form.method.new) {
      openModal(modalTypes.ModalAddMethod, {
        method: form.method.value,
        back: modalTypes.ModalMakePayment,
        backData: {
          form: { ...form, method: null }, // Clear the method selection
          mode: mode,
          account: props.account,
          amount: props.amount,
          selectedPlan: selectedPlan,
          offerId: offerId,
          back: back,
          backData: backData,
          refreshMethods: true, // Flag to indicate we should refresh methods
        },
      });
    } else {
      await _getDisclosureText();
      // If ACH (checking/savings), show specialized disclosure confirmation
      if (
        form?.method?.method_type === "CHECKING" ||
        form?.method?.method_type === "SAVINGS"
      ) {
        // Prepare and verify ACH disclosure text matches expected template
        const todayStr = formatMMDDYYYY(new Date());
        const agencyName = mainStore?.agencyDetails?.name;
        const agencyPhone =
          mainStore?.agencyDetails?.phone || mainStore?.profile?.agency?.phone;
        const agencyWebsite = mainStore?.agencyDetails?.website
          ? mainStore.agencyDetails.website
          : undefined;
        const agencyAddressLine =
          mainStore?.agencyDetails?.address &&
          mainStore?.agencyDetails?.city &&
          mainStore?.agencyDetails?.state &&
          mainStore?.agencyDetails?.zip
            ? `${mainStore.agencyDetails.address}, ${mainStore.agencyDetails.city} ${mainStore.agencyDetails.state} ${mainStore.agencyDetails.zip}`
            : mainStore?.profile?.agency?.address &&
                mainStore?.profile?.agency?.city &&
                mainStore?.profile?.agency?.state &&
                mainStore?.profile?.agency?.zip
              ? `${mainStore.profile.agency.address}, ${mainStore.profile.agency.city} ${mainStore.profile.agency.state} ${mainStore.profile.agency.zip}`
              : undefined;
        const methodDesc = form?.method?.label || "";
        const creditorName =
          form?.account?.creditorName ||
          getCreditorNameFromLabel(form?.account?.label || "");

        const expectedParagraphs = buildAchDisclosureParagraphs(
          agencyName,
          methodDesc,
          creditorName,
          todayStr,
          agencyWebsite,
          agencyAddressLine,
          agencyPhone
        );

        const actualParagraphs = buildAchDisclosureParagraphs(
          agencyName,
          methodDesc,
          creditorName,
          todayStr,
          agencyWebsite,
          agencyAddressLine,
          agencyPhone
        );

        const expected = expectedParagraphs.join("\n\n").trim();
        const actual = actualParagraphs.join("\n\n").trim();
        setAchVerifyError(
          expected === actual ? null : "ACH disclosure text mismatch"
        );

        setStep("ach-confirm");
      } else {
        setStep("confirm");
      }
    }
  }, [schedule, form]);

  const makePayment = useCallback(async () => {
    if (!processing) {
      setProcessing(true);

      try {
        let cleanAmount = form.amount;
        cleanAmount = cleanAmount.replaceAll("$", "");
        cleanAmount = cleanAmount.replaceAll(",", "");

        // Handle settlement offer submission with new format
        if (mode === "settlement-offer") {
          const submitAmount = Number(cleanAmount);

          // Build the payload in the new format
          const payload = {
            amount: submitAmount,
            method_id: form.method.value,
            payments: [
              {
                amount: submitAmount,
                conv_fee: convFee?.fee ?? 0,
                scheduled: schedule ? form.date : formatMMDDYYYY(new Date()),
              },
            ],
          };

          const submitResponse = await API.post(
            `/accounts/${form.account.value}/offer`,
            payload
          );

          // Get full account details for modal navigation
          const accountDetails = props.account ||
            accounts.find((acc) => acc.value === form.account.value) || {
              id: form.account.value,
            };

          // Handle the response based on status
          // Missing or 0 = declined, 1 = accepted, 2 = payment declined
          const status = submitResponse.data.status;

          if (status === 1) {
            // Track successful offer
            trackOfferSubmitted({
              amount: submitAmount,
              outcome: "accepted",
              payment_date: schedule ? form.date : formatMMDDYYYY(new Date()),
            });
            
            // Accepted - show success modal with confetti
            openModal(modalTypes.ModalSettlementAccepted, {
              account: accountDetails,
              submitAmount,
              receiptId: submitResponse.data.id || submitResponse.data.ref_id,
              paymentMethod: form.method.label,
              scheduledDate: schedule ? form.date : null,
            });
          } else if (status === 2) {
            // Track payment declined
            trackOfferSubmitted({
              amount: submitAmount,
              outcome: "payment_declined",
              payment_date: schedule ? form.date : formatMMDDYYYY(new Date()),
            });
            
            // Payment declined - show payment method error modal
            openModal(modalTypes.ModalPaymentDeclined, {
              account: accountDetails,
              submitAmount,
              paymentMethod: form.method.label,
              error: submitResponse.data.error || "Payment method was declined",
              mode: "settlement-offer",
              back: modalTypes.ModalMakePayment,
              backData: {
                form: form,
                mode: mode,
                account: props.account,
                amount: props.amount,
                back: back,
                backData: backData,
              },
            });
          } else {
            // Track offer declined
            trackOfferSubmitted({
              amount: submitAmount,
              outcome: "declined",
              payment_date: schedule ? form.date : formatMMDDYYYY(new Date()),
            });
            
            // Status is 0, undefined, or any other value - declined/rejected
            openModal(modalTypes.ModalSettlementDeclined, {
              account: accountDetails,
              submitAmount,
              error: submitResponse.data.error || "Your offer was not accepted",
            });
          }
          return; // Exit early for settlement offers
        }

        // This is not for a payment plan
        if (selectedPlan === undefined) {
          const scheduledDate = schedule
            ? form.date
            : formatMMDDYYYY(new Date());

          const payload = {
            method_id: form.method.value,
            payments: [
              {
                amount: parseFloat(cleanAmount),
                conv_fee: convFee?.fee ?? 0,
                scheduled: scheduledDate,
              },
            ],
          };

          // Preserve offer id if provided (server may expect it)
          if (offerId !== undefined) {
            payload.offer_id = offerId;
          }

          const response = await API.post(
            `/accounts/${form.account.value}/payments`,
            payload
          );
          if (response.data.result_code) {
            // Track failed payment
            trackPaymentSubmitted({
              amount: parseFloat(cleanAmount),
              outcome: "failed",
              payment_type: "single",
              scheduled_date: scheduledDate,
            });
            setError(response.data.message);
          } else {
            // Track successful payment
            trackPaymentSubmitted({
              amount: parseFloat(cleanAmount),
              outcome: "accepted",
              payment_type: "single",
              scheduled_date: scheduledDate,
            });
            setPaymentResponse(response.data);
            setStep("receipt");
            setReceiptId(
              response.data.receipt_id ||
                response.data.id ||
                response.data.payment_id ||
                ""
            );
          }
        }

        // This is for a payment plan → send to /payments with array of payments
        if (selectedPlan !== undefined && offerId === undefined) {
          const { monthlyPayment, firstMonth, numberMonths } =
            calculatePaymentPlan(selectedPlan);

          const initialDate =
            schedule && form.date ? parseMMDDYYYY(form.date) : new Date();

          const payments = [];
          payments.push({
            amount: Number(firstMonth),
            conv_fee: convFee?.fee ?? 0,
            scheduled: schedule ? form.date : formatMMDDYYYY(initialDate),
          });
          for (let i = 1; i < numberMonths; i++) {
            const recDate = computeRecurringDate(
              initialDate,
              form.recurring_method?.value || "1st",
              i
            );
            payments.push({
              amount: Number(monthlyPayment),
              conv_fee: convFee?.fee ?? 0,
              scheduled: recDate,
            });
          }

          const payload = {
            method_id: form.method.value,
            payments,
          };

          const response = await API.post(
            `/accounts/${form.account.value}/payments`,
            payload
          );
          if (response.data.result_code) {
            // Track failed payment plan
            trackPaymentSubmitted({
              amount: payments.reduce((sum, p) => sum + p.amount, 0),
              outcome: "failed",
              payment_type: "payment_plan",
              scheduled_date: schedule ? form.date : formatMMDDYYYY(initialDate),
              plan_months: numberMonths,
              monthly_payment: monthlyPayment,
            });
            setError(response.data.message);
          } else {
            // Track successful payment plan
            trackPaymentSubmitted({
              amount: payments.reduce((sum, p) => sum + p.amount, 0),
              outcome: "accepted",
              payment_type: "payment_plan",
              scheduled_date: schedule ? form.date : formatMMDDYYYY(initialDate),
              plan_months: numberMonths,
              monthly_payment: monthlyPayment,
            });
            setPaymentResponse(response.data);
            setReceiptId(
              response.data.receipt_id ||
                response.data.id ||
                response.data.payment_id ||
                ""
            );
            setStep("receipt");
          }
        }
      } catch (e) {
        console.log(e);
      }

      setProcessing(false);
    }
  }, [processing, form, selectedPlan, offerId]);

  const paymentPlanText = () => {
    const { monthlyPayment, firstMonth, numberMonths } =
      calculatePaymentPlan(selectedPlan);
    return (
      <div className="text-center">
        <p className="text-white font-medium">
          One payment of{" "}
          <span className="text-xl font-bold text-blue-400">${firstMonth}</span>
        </p>
        <p className="text-gray-300 mt-1">
          Then{" "}
          <span className="text-white font-semibold">
            ${formatMoney(monthlyPayment)}/month
          </span>{" "}
          for {numberMonths - 1} months
        </p>
      </div>
    );
  };

  const _getDisclosureText = useCallback(async () => {
    try {
      setFeeLoading(true);
      let cleanAmount = form.amount;
      if (typeof cleanAmount === "string") {
        cleanAmount = cleanAmount.replaceAll("$", "");
        cleanAmount = cleanAmount.replaceAll(",", "");
      }
      const response = await API.post("/fees/convenience", {
        PaymentMethod: form.method?.method_type || form.method?.value,
        Amount: parseFloat(cleanAmount),
        AccountId: form.account.value,
        BillingZip: form.method?.billing_zip || "",
        BillingState: form.method?.billing_state || "",
      });
      if (response.data.charge_fee) {
        setConfFee({ fee: response.data.fee, text: response.data.disclosures });
      } else {
        setConfFee(null);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setFeeLoading(false);
    }
  }, [form]);

  // On focus of this tab, fetch the profile
  React.useEffect(() => {
    fetchAccounts().then();
    fetchMethods().then();
  }, []);

  // Check if we need to refresh methods after adding a new one
  React.useEffect(() => {
    if (props.refreshMethods) {
      fetchMethods().then();
    }
  }, [props.refreshMethods]);

  // Sync form state when a new payment method is passed via props (after adding a method)
  React.useEffect(() => {
    if (props.form?.method && props.form.method !== form.method) {
      setForm((prevState) => ({
        ...prevState,
        method: props.form.method,
      }));
    }
  }, [props.form?.method]);

  // Fetch existing scheduled payments when account changes
  React.useEffect(() => {
    const accountId = form.account?.value || selectedAccount;
    if (accountId) {
      fetchExistingScheduledPayments(accountId);
    }
  }, [form.account?.value, selectedAccount]);

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "rgba(31, 41, 55, 0.5)",
      borderColor: state.isFocused
        ? "rgba(59, 130, 246, 0.5)"
        : "rgba(75, 85, 99, 0.5)",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
      "&:hover": {
        borderColor: "rgba(59, 130, 246, 0.5)",
      },
      minHeight: "48px",
      borderRadius: "0.75rem",
      border: "2px solid",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "rgba(17, 24, 39, 0.95)",
      backdropFilter: "blur(12px)",
      borderRadius: "0.75rem",
      border: "1px solid rgba(75, 85, 99, 0.5)",
      marginTop: "4px",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "rgba(59, 130, 246, 0.3)"
        : state.isFocused
          ? "rgba(59, 130, 246, 0.1)"
          : "transparent",
      color: state.isSelected ? "#fff" : "rgba(209, 213, 219, 1)",
      "&:hover": {
        backgroundColor: "rgba(59, 130, 246, 0.2)",
        color: "#fff",
      },
      padding: "10px 12px",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#fff",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "rgba(156, 163, 175, 1)",
    }),
    input: (provided) => ({
      ...provided,
      color: "#fff",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "rgba(156, 163, 175, 1)",
      "&:hover": {
        color: "#fff",
      },
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: "rgba(75, 85, 99, 0.5)",
    }),
  };

  if (step === "receipt" && receiptId !== "" && !schedule) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-6"
      >
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                Payment Successful!
              </h3>
              <p className="text-gray-400 text-sm">
                Your payment has been processed
              </p>
            </div>
          </div>
          {(() => {
            const isAch =
              form?.method?.method_type === "CHECKING" ||
              form?.method?.method_type === "SAVINGS";
            if (!isAch) {
              const transactionRef =
                paymentResponse?.ref_id || paymentResponse?.reference;
              return (
                <>
                  <p className="text-gray-300 mb-4">
                    Your payment has been processed and applied to your account.
                  </p>
                  <div className="space-y-3">
                    <div className="bg-gray-800/50 rounded-lg px-4 py-3">
                      <span className="text-gray-400 text-sm">
                        Confirmation ID
                      </span>
                      <p className="text-white font-mono mt-1">{receiptId}</p>
                    </div>
                    {transactionRef && (
                      <div className="bg-gray-800/50 rounded-lg px-4 py-3">
                        <span className="text-gray-400 text-sm">
                          Transaction Reference
                        </span>
                        <p className="text-white font-mono mt-1">
                          {transactionRef}
                        </p>
                      </div>
                    )}
                  </div>
                </>
              );
            }

            const paymentId =
              paymentResponse?.id || paymentResponse?.receipt_id || receiptId;
            const transactionRef =
              paymentResponse?.ref_id || paymentResponse?.reference;
            const dateAuthorized = formatMMDDYYYY(new Date());

            // Build scheduled transactions list for success view
            const scheduledItems = [];
            if (selectedPlan !== undefined) {
              const { monthlyPayment, firstMonth, numberMonths } =
                calculatePaymentPlan(selectedPlan);
              const initialDate =
                schedule && form.date ? parseMMDDYYYY(form.date) : new Date();
              scheduledItems.push({
                date: schedule ? form.date : formatMMDDYYYY(initialDate),
                amount: `$${formatMoney(firstMonth)}`,
              });
              for (let i = 1; i < numberMonths; i++) {
                const recDate = computeRecurringDate(
                  initialDate,
                  form.recurring_method?.value || "1st",
                  i
                );
                scheduledItems.push({
                  date: recDate,
                  amount: `$${formatMoney(monthlyPayment)}`,
                });
              }
            } else {
              const sched = schedule ? form.date : formatMMDDYYYY(new Date());
              const amt = `$${formatMoney(
                String(form.amount).replaceAll("$", "").replaceAll(",", "")
              )}`;
              scheduledItems.push({ date: sched, amount: amt });
            }

            return (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-bold text-white">
                    Payment ID:
                  </span>
                  <span className="text-sm text-white">{paymentId}</span>
                </div>
                {transactionRef && (
                  <div className="flex justify-between">
                    <span className="text-sm font-bold text-white">
                      Transaction Reference:
                    </span>
                    <span className="text-sm text-white">{transactionRef}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm font-bold text-white">
                    Date Authorized:
                  </span>
                  <span className="text-sm text-white">{dateAuthorized}</span>
                </div>
                <div className="mt-4">
                  <p className="text-sm font-bold text-white underline">
                    Authorized Transactions:
                  </p>
                  <div className="flex justify-between">
                    <span className="text-sm font-bold text-white">
                      Transaction Date
                    </span>
                    <span className="text-sm font-bold text-white">
                      Transaction Amount
                    </span>
                  </div>
                  {scheduledItems.map((item, idx) => (
                    <div
                      key={`ach-succ-${idx}`}
                      className="flex justify-between"
                    >
                      <span className="text-sm text-white">{item.date}</span>
                      <span className="text-sm text-white">{item.amount}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-sm font-bold text-white">
                    Authorized Account:
                  </span>
                  <span className="text-sm text-white">
                    {form?.method?.label}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  It is recommended that you print or take a screenshot of this
                  confirmation and maintain it for your records.
                </p>
              </div>
            );
          })()}
        </div>
        <button
          onClick={() => _closeModal()}
          className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl transition-all duration-200"
        >
          Close
        </button>
      </motion.div>
    );
  } else if (step === "payment-plan-success") {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-6"
      >
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                Payment Plan Active!
              </h3>
              <p className="text-gray-400 text-sm">
                Your payment plan has been set up
              </p>
            </div>
          </div>
          <p className="text-gray-300">
            Your payment plan was successfully registered and is now active.
          </p>
        </div>
        <button
          onClick={() => _closeModal()}
          className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl transition-all duration-200"
        >
          Close
        </button>
      </motion.div>
    );
  } else if (step === "receipt" && receiptId !== "" && schedule) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-6"
      >
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
              <Calendar className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                Payment Scheduled
              </h3>
              <p className="text-gray-400 text-sm">
                Your payment has been scheduled
              </p>
            </div>
          </div>
          <p className="text-gray-300 mb-4">
            Your payment has been scheduled for{" "}
            <span className="text-white font-semibold">{form.date}</span>. We
            will attempt payment on this date.
          </p>
          <div className="bg-gray-800/50 rounded-lg px-4 py-3">
            <span className="text-gray-400 text-sm">Confirmation ID</span>
            <p className="text-white font-mono mt-1">{receiptId}</p>
          </div>
        </div>
        <button
          onClick={() => _closeModal()}
          className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl transition-all duration-200"
        >
          Close
        </button>
      </motion.div>
    );
  } else if (step === "confirm") {
    let amount = form.amount;
    let additionalText;
    let displayAmount;
    if (selectedPlan !== undefined) {
      const { monthlyPayment, firstMonth, numberMonths } =
        calculatePaymentPlan(selectedPlan);
      amount = firstMonth;
      const monthlyPaymentFormatted = `$${formatMoney(monthlyPayment)}`;
      additionalText = `You will then be charged ${monthlyPaymentFormatted} on the ${
        form.recurring_method.label
      } for ${numberMonths - 1} months.`;
      displayAmount = `$${firstMonth}`;
    } else {
      const clean = String(amount).replaceAll("$", "").replaceAll(",", "");
      displayAmount = `$${formatMoney(clean)}`;
    }

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-6"
      >
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
              {schedule ? (
                <Clock className="w-5 h-5 text-blue-400" />
              ) : (
                <Send className="w-5 h-5 text-blue-400" />
              )}
            </div>
            <h3 className="text-lg font-semibold text-white">
              {mode === "settlement-offer"
                ? "Confirm Offer Submission"
                : schedule
                  ? "Confirm Scheduled Payment"
                  : "Confirm Payment"}
            </h3>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-700">
              <span className="text-gray-400">Amount</span>
              <span className="text-xl font-semibold text-white">
                {displayAmount}
              </span>
            </div>

            <div className="py-2 border-b border-gray-700">
              <span className="block text-gray-400 mb-1">Payment Method</span>
              <span className="block text-white whitespace-normal break-words leading-snug">
                {form.method.label}
              </span>
            </div>

            <div className="py-2 border-b border-gray-700">
              <span className="block text-gray-400 mb-1">Account</span>
              <div className="text-right">
                <span className="block text-white">
                  {form.account.label.split(" (Balance:")[0]}
                </span>
                {form.account.label.includes("(Balance:") && (
                  <span className="block text-gray-300 text-sm mt-1">
                    Balance: $
                    {form.account.label
                      .split("(Balance: $")[1]
                      ?.replace(")", "")}
                  </span>
                )}
              </div>
            </div>

            {schedule && (
              <div className="flex justify-between items-center py-2 border-b border-gray-700">
                <span className="text-gray-400">Scheduled Date</span>
                <span className="text-white">{form.date}</span>
              </div>
            )}
          </div>

          {additionalText && (
            <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-300">{additionalText}</p>
            </div>
          )}

          {convFee && convFee.text && (
            <div
              className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm text-yellow-300"
              dangerouslySetInnerHTML={{ __html: convFee.text }}
            />
          )}

          <div className="mt-4 p-3 bg-gray-900/50 rounded-lg">
            <p className="text-sm text-gray-400">
              {mode === "settlement-offer"
                ? "Your offer will be submitted immediately"
                : schedule
                  ? `Your payment will be processed on ${form.date}`
                  : "Your payment will be processed immediately"}
            </p>
          </div>

          {/* Scheduled Payments Cancellation Warning */}
          {existingScheduledPayments.length > 0 && (
            <div className="mt-4 mb-4 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-orange-300 mb-1">
                    Warning: Scheduled Payments Will Be Canceled
                  </p>
                  <p className="text-xs text-orange-200 leading-relaxed">
                    You have {existingScheduledPayments.length} scheduled payment{existingScheduledPayments.length > 1 ? 's' : ''} that will be canceled when this payment is processed.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Resolution Offer Disclaimer in Confirmation */}
          {(offerId || mode === "settlement-offer") && (
            <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-300 mb-2">
                    Important Notice:
                  </p>
                  <p className="text-xs text-yellow-200 leading-relaxed">
                    You can make an offer to resolve your account for less than
                    the full amount you owe. If your offer is accepted, payment
                    for this arrangement will be processed immediately. If you
                    leave before finalizing your payment, you may be able to
                    present a new offer in the future. We are not obligated to
                    renew our acceptance of your offer in the future and our
                    acceptance of your offer does not affect or take away other
                    rights previously disclosed to you.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-300">{error}</p>
          </motion.div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => makePayment()}
            disabled={processing}
            className={classNames(
              "flex-1 py-3 font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2",
              processing
                ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white"
            )}
          >
            {processing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                {mode === "settlement-offer"
                  ? "Submit Offer"
                  : "Confirm Payment"}
              </>
            )}
          </button>

          <button
            onClick={() => {
              setError(null);
              setStep("payment");
            }}
            disabled={processing}
            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Go Back
          </button>
        </div>
      </motion.div>
    );
  }

  if (step === "ach-confirm") {
    const amountClean = String(form.amount)
      .replaceAll("$", "")
      .replaceAll(",", "");
    const todayStr = formatMMDDYYYY(new Date());
    const agencyName = mainStore?.agencyDetails?.name;
    const agencyPhone =
      mainStore?.agencyDetails?.phone || mainStore?.profile?.agency?.phone;
    const agencyWebsite = mainStore?.agencyDetails?.website
      ? mainStore.agencyDetails.website
      : undefined;
    const agencyAddressLine =
      mainStore?.agencyDetails?.address &&
      mainStore?.agencyDetails?.city &&
      mainStore?.agencyDetails?.state &&
      mainStore?.agencyDetails?.zip
        ? `${mainStore.agencyDetails.address}, ${mainStore.agencyDetails.city} ${mainStore.agencyDetails.state} ${mainStore.agencyDetails.zip}`
        : mainStore?.profile?.agency?.address &&
            mainStore?.profile?.agency?.city &&
            mainStore?.profile?.agency?.state &&
            mainStore?.profile?.agency?.zip
          ? `${mainStore.profile.agency.address}, ${mainStore.profile.agency.city} ${mainStore.profile.agency.state} ${mainStore.profile.agency.zip}`
          : undefined;
    const methodDesc = form?.method?.label || "";
    const creditorName =
      form?.account?.creditorName ||
      getCreditorNameFromLabel(form?.account?.label || "");
    const paragraphs = buildAchDisclosureParagraphs(
      agencyName,
      methodDesc,
      creditorName,
      todayStr,
      agencyWebsite,
      agencyAddressLine,
      agencyPhone
    );

    // Build scheduled transactions list
    const scheduledItems = [];
    if (selectedPlan !== undefined) {
      const { monthlyPayment, firstMonth, numberMonths } =
        calculatePaymentPlan(selectedPlan);
      const initialDate =
        schedule && form.date ? parseMMDDYYYY(form.date) : new Date();
      scheduledItems.push({
        date: schedule ? form.date : formatMMDDYYYY(initialDate),
        amount: `$${formatMoney(firstMonth)}`,
      });
      for (let i = 1; i < numberMonths; i++) {
        const recDate = computeRecurringDate(
          initialDate,
          form.recurring_method?.value || "1st",
          i
        );
        scheduledItems.push({
          date: recDate,
          amount: `$${formatMoney(monthlyPayment)}`,
        });
      }
    } else {
      const sched = schedule ? form.date : formatMMDDYYYY(new Date());
      scheduledItems.push({
        date: sched,
        amount: `$${formatMoney(amountClean)}`,
      });
    }

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-6"
      >
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
              <Send className="w-5 h-5 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">
              Confirm ACH Authorization
            </h3>
          </div>

          <div className="space-y-3">
            <div className="py-2 border-b border-gray-700">
              <span className="block text-gray-400 mb-1">Payment Method</span>
              <span className="block text-white whitespace-normal break-words leading-snug">
                {form.method.label}
              </span>
            </div>
            <div className="py-2 border-b border-gray-700">
              <span className="block text-gray-400 mb-1">Account</span>
              <div className="text-right">
                <span className="block text-white">
                  {form.account.label.split(" (Balance:")[0]}
                </span>
                {form.account.label.includes("(Balance:") && (
                  <span className="block text-gray-300 text-sm mt-1">
                    Balance: $
                    {form.account.label
                      .split("(Balance: $")[1]
                      ?.replace(")", "")}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-3 text-sm text-gray-300">
            <p className="text-white font-semibold">ACH Disclosure</p>
            {paragraphs.map((p, i) => (
              <div key={`ach-section-${i}`} className="space-y-2">
                <p className={i === 0 ? "text-sm font-bold" : "text-sm"}>{p}</p>
                {i === 1 && (
                  <div className="mt-1">
                    <div className="flex justify-between">
                      <span className="text-sm font-bold text-white">
                        Transaction Date
                      </span>
                      <span className="text-sm font-bold text-white">
                        Transaction Amount
                      </span>
                    </div>
                    {scheduledItems.map((item, idx) => (
                      <div
                        key={`sched-${idx}`}
                        className="flex justify-between"
                      >
                        <span className="text-sm text-white">{item.date}</span>
                        <span className="text-sm text-white">
                          {item.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {achVerifyError && (
              <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300">
                {achVerifyError}
              </div>
            )}
          </div>
        </div>

        {/* Scheduled Payments Cancellation Warning */}
        {existingScheduledPayments.length > 0 && (
          <div className="mt-4 mb-4 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-orange-300 mb-1">
                  Warning: Scheduled Payments Will Be Canceled
                </p>
                <p className="text-xs text-orange-200 leading-relaxed">
                  You have {existingScheduledPayments.length} scheduled payment{existingScheduledPayments.length > 1 ? 's' : ''} that will be canceled when this payment is processed.
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-300">{error}</p>
          </motion.div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => makePayment()}
            disabled={processing || !!achVerifyError}
            className={classNames(
              "flex-1 py-3 font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2",
              processing || achVerifyError
                ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white"
            )}
          >
            {processing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                {mode === "settlement-offer"
                  ? "Submit Offer"
                  : "Confirm Payment"}
              </>
            )}
          </button>

          <button
            onClick={() => {
              setError(null);
              setStep("payment");
            }}
            disabled={processing}
            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Go Back
          </button>
        </div>
      </motion.div>
    );
  }

  const scheduledInput = () => {
    // Calculate min and max dates
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 45);

    // Convert MM/DD/YYYY string to Date object
    const getDateValue = () => {
      if (!form.date) return null;
      const parsed = parseMMDDYYYY(form.date);
      return parsed;
    };

    // Handle date change from DatePicker
    const handleDateChange = (date) => {
      if (!date) {
        setDate("");
        return;
      }
      const formattedDate = formatMMDDYYYY(date);
      setDate(formattedDate);
    };

    // Custom input component for DatePicker
    const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
      <div className="relative">
        <input
          ref={ref}
          className={classNames(
            "w-full px-4 py-3 bg-gray-800/50 border-2 rounded-xl",
            "text-white placeholder-gray-500 cursor-pointer",
            "focus:outline-none focus:border-blue-500 focus:bg-gray-800/70",
            "transition-all duration-200",
            dateError ? "border-red-500" : "border-gray-700"
          )}
          value={value}
          onClick={onClick}
          readOnly
          placeholder="Select a date"
        />
        <Calendar
          className={classNames(
            "absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none",
            dateError ? "text-red-500" : "text-gray-400"
          )}
        />
      </div>
    ));

    return (
      <div className="relative custom-datepicker">
        <DatePicker
          selected={getDateValue()}
          onChange={handleDateChange}
          minDate={tomorrow}
          maxDate={maxDate}
          customInput={<CustomInput />}
          dateFormat="MM/dd/yyyy"
          placeholderText="Select a date"
          popperClassName="custom-datepicker-popper"
          popperPlacement="bottom-start"
        />

        {dateError && (
          <p className="mt-2 text-sm text-red-400">
            Please select a future date within 45 days
          </p>
        )}
      </div>
    );
  };

  const checkDisabled = () => {
    if (
      form.account === "" ||
      form.account === undefined ||
      form.method === undefined ||
      processing ||
      dateError
    ) {
      return true;
    }
    if (selectedPlan !== undefined && form.recurring_method === undefined) {
      return true;
    }
    if (schedule && (form.date === undefined || form.date === "")) {
      return true;
    }
    return false;
  };

  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemAnimation = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <motion.div
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="p-6"
    >
      <motion.div variants={itemAnimation} className="space-y-4">
        {/* Account Selection */}
        <div>
          <label className="block text-sm font-semibold text-gray-300 uppercase tracking-wider mb-2">
            Select Account
          </label>
          <Select
            id="select-account"
            placeholder="Select Account"
            options={accounts}
            onChange={(e) => setForm({ ...form, account: e })}
            styles={customStyles}
            value={form.account}
          />
        </div>

        {/* Payment Method Selection */}
        <div>
          <label className="block text-sm font-semibold text-gray-300 uppercase tracking-wider mb-2">
            Payment Method
          </label>
          <Select
            id="select-payment"
            placeholder="Payment Method"
            options={methods}
            onChange={(e) => setForm({ ...form, method: e })}
            styles={customStyles}
            value={form.method}
            isDisabled={feeLoading || processing}
          />
        </div>

        {/* Amount Input */}
        {mode !== "payment-plan" && offerId === undefined && (
          <div>
            <label className="block text-sm font-semibold text-gray-300 uppercase tracking-wider mb-2">
              Payment Amount
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <DollarSign className="w-5 h-5 text-gray-500" />
              </div>
              <CurrencyInput
                id="amount"
                placeholder="0.00"
                type="text"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                maskOptions={{ prefix: "" }}
                className={classNames(
                  "w-full pl-12 pr-4 py-3 bg-gray-800/50 border-2 rounded-xl",
                  "text-white placeholder-gray-500",
                  "focus:outline-none focus:border-blue-500 focus:bg-gray-800/70",
                  "transition-all duration-200",
                  error ? "border-red-500" : "border-gray-700",
                  feeLoading ? "opacity-70" : ""
                )}
                disabled={feeLoading || processing}
              />
              {feeLoading && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                </div>
              )}
              {error && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>
      {/* Payment Plan Options */}
      {selectedPlan !== undefined && (
        <motion.div variants={itemAnimation} className="mt-6 space-y-4">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            {paymentPlanText()}
          </div>

          {/* Schedule Toggle */}
          <div>
            <button
              onClick={() => toggleSchedule(!schedule)}
              className={classNames(
                "w-full p-4 rounded-xl border-2 transition-all duration-200",
                "flex items-center justify-between group",
                schedule
                  ? "bg-blue-500/10 border-blue-500/50 hover:bg-blue-500/20"
                  : "bg-gray-800/50 border-gray-700 hover:border-gray-600 hover:bg-gray-800/70"
              )}
            >
              <div className="flex items-center gap-3">
                <Calendar
                  className={classNames(
                    "w-5 h-5 transition-colors",
                    schedule ? "text-blue-400" : "text-gray-400"
                  )}
                />
                <span className="text-white font-medium">
                  Schedule initial payment for later
                </span>
              </div>
              <div
                className={classNames(
                  "ml-4 px-3 py-1 rounded-lg text-sm font-semibold transition-colors",
                  schedule
                    ? "bg-blue-500/20 text-blue-400"
                    : "bg-gray-700 text-gray-400"
                )}
              >
                {schedule ? "ON" : "OFF"}
              </div>
            </button>
            {schedule && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4"
              >
                {scheduledInput()}
                {dateError && (
                  <p className="mt-2 text-sm text-red-400">
                    Please select a future date within 45 days
                  </p>
                )}
              </motion.div>
            )}
          </div>

          {/* Recurring Payment Method */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 uppercase tracking-wider mb-2">
              Recurring Payment Date
            </label>
            <Select
              id="recurring-method"
              placeholder="When should recurring payments happen?"
              styles={customStyles}
              value={form.recurring_method}
              onChange={(e) => setForm({ ...form, recurring_method: e })}
              options={[
                { value: "1st", label: "1st of each month" },
                { value: "15th", label: "15th of each month" },
                { value: "last", label: "Last day of month" },
              ]}
            />
          </div>
        </motion.div>
      )}

      {/* One-time Payment Schedule Option */}
      {selectedPlan === undefined && (
        <motion.div variants={itemAnimation} className="mt-6">
          <button
            onClick={() => toggleSchedule(!schedule)}
            className={classNames(
              "w-full p-4 rounded-xl border-2 transition-all duration-200",
              "flex items-center justify-between group",
              schedule
                ? "bg-blue-500/10 border-blue-500/50 hover:bg-blue-500/20"
                : "bg-gray-800/50 border-gray-700 hover:border-gray-600 hover:bg-gray-800/70"
            )}
          >
            <div className="flex items-center gap-3">
              <Clock
                className={classNames(
                  "w-5 h-5 transition-colors",
                  schedule ? "text-blue-400" : "text-gray-400"
                )}
              />
              <span className="text-white font-medium">
                Schedule payment for later
              </span>
            </div>
            <div
              className={classNames(
                "ml-4 px-3 py-1 rounded-lg text-sm font-semibold transition-colors",
                schedule
                  ? "bg-blue-500/20 text-blue-400"
                  : "bg-gray-700 text-gray-400"
              )}
            >
              {schedule ? "ON" : "OFF"}
            </div>
          </button>
          {schedule && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4"
            >
              {scheduledInput()}
              {dateError && (
                <p className="mt-2 text-sm text-red-400">
                  Please select a future date within 45 days
                </p>
              )}
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Offer Resolution Notice */}
      {(offerId || mode === "settlement-offer") && (
        <motion.div variants={itemAnimation} className="mt-6 space-y-4">
          {/* Resolution Payment Notice - Only show when no date error */}
          {(!schedule || !dateError) && (
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-center">
              <p className="text-green-300 font-medium">
                {mode === "settlement-offer" ? (
                  <>
                    If your offer of{" "}
                    <span className="text-white font-bold">${form.amount}</span>{" "}
                    to resolve this account is accepted, your payment will be
                    processed{" "}
                    {schedule && form.date ? (
                      <>
                        on{" "}
                        <span className="text-white font-bold">
                          {form.date}
                        </span>
                      </>
                    ) : (
                      "immediately"
                    )}
                    .
                  </>
                ) : (
                  <>
                    This payment of{" "}
                    <span className="text-white font-bold">${form.amount}</span>{" "}
                    will resolve your account
                  </>
                )}
              </p>
            </div>
          )}

          {/* Resolution Offer Disclaimer */}
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-300 mb-2">
                  Resolution Offer Terms:
                </p>
                <p className="text-xs text-yellow-200 leading-relaxed">
                  You can make an offer to resolve your account for less than
                  the full amount you owe. If accepted, payment for this
                  arrangement will be processed at the date you have selected.
                  If you leave before finalizing your payment, you may be able
                  to present a new offer in the future. We are not obligated to
                  renew our acceptance of your offer in the future and our
                  acceptance of your offer does not affect or take away other
                  rights previously disclosed to you.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Submit Button */}
      <motion.div variants={itemAnimation} className="mt-6">
        <div className="flex gap-3">
          {back && (
            <button
              onClick={() => openModal(back, backData)}
              disabled={processing}
              className="px-6 py-3 font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white"
            >
              <ArrowLeft className="w-5 h-5" />
              Go Back
            </button>
          )}
          <button
            onClick={() => nextStep()}
            disabled={checkDisabled() || feeLoading}
            className={classNames(
              "flex-1 py-3 font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2",
              checkDisabled() || feeLoading
                ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl"
            )}
          >
            {processing || feeLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {processing ? "Processing..." : "Calculating fees..."}
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FormMakePayment;
